import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { openDialog, useAppDispatch } from '@fly/store';
import { Separator } from '@radix-ui/themes';

import { ReleaseDistributionDialogContent } from '../components/ArtifactVersionsPage/components/DistributionForm/ReleaseDistributionDialogContent';
import { type OpenReleaseDistributionDialogParams } from '../components/ArtifactVersionsPage/components/DistributionForm/distributionFormKeys';

export type {
  OpenArtifactDistributionDialogParams,
  OpenReleaseDistributionDialogParams,
} from '../components/ArtifactVersionsPage/components/DistributionForm/distributionFormKeys';

export function useReleaseDistributionDialog() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const openReleaseDistributionDialog = useCallback(
    (params: OpenReleaseDistributionDialogParams) => {
      const reopenDistributionDialog = () => {
        openReleaseDistributionDialog(params);
      };

      dispatch(
        openDialog({
          title: t('app.dashboard.artifacts.distribution_form.title', {
            defaultValue: 'Manage Distribution',
          }),
          description: t('app.dashboard.artifacts.distribution_form.subtitle', {
            defaultValue: 'Distribute your artifact to a public audience',
          }),
          content: (
            <>
              <Separator
                orientation='horizontal'
                className='mb-3 mt-3 w-full bg-[var(--slate-a5)]'
              />
              <ReleaseDistributionDialogContent
                releaseId={params.releaseId}
                repoName={params.repoName}
                repoOwner={params.repoOwner}
                workflowName={params.workflowName}
                workflowRunNumber={params.workflowRunNumber}
                reopenDistributionDialog={reopenDistributionDialog}
              />
            </>
          ),
          showCloseDialog: true,
        })
      );
    },
    // Recursive reopen captures latest openReleaseDistributionDialog per hook instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable reopen via self-call with same params
    [dispatch, t]
  );

  return { openReleaseDistributionDialog };
}
